<script>
import { MANAGEMENT, SECRET } from '@shell/config/types';
import CreateEditView from '@shell/mixins/create-edit-view';
import GlobalRoleBindings from '@shell/components/GlobalRoleBindings.vue';
import ChangePassword from '@shell/components/form/ChangePassword';
import { LabeledInput } from '@components/Form/LabeledInput';
import CruResource from '@shell/components/CruResource';
import { exceptionToErrorsArray } from '@shell/utils/error';
import { _CREATE, _EDIT } from '@shell/config/query-params';
import Loading from '@shell/components/Loading';
import { wait } from '@shell/utils/async';
import { base64Encode } from '@shell/utils/crypto';

export default {
  components: {
    ChangePassword, GlobalRoleBindings, CruResource, LabeledInput, Loading
  },

  mixins: [
    CreateEditView
  ],

  inheritAttrs: false,

  data() {
    const showGlobalRoles = !!this.$store.getters[`management/schemaFor`](MANAGEMENT.GLOBAL_ROLE);

    return {
      showGlobalRoles,
      form: {
        username:    this.value.username,
        description: this.value.description,
        displayName: this.value.displayName,
        password:    {
          password:          '',
          userChangeOnLogin: false,
        }
      },
      validation: {
        password:     false,
        roles:        !showGlobalRoles,
        rolesChanged: false,
      }
    };
  },

  computed: {
    valid() {
      const valid = this.credentialsValid && this.rolesValid;

      if (this.isCreate) {
        return valid;
      }
      if (this.isEdit) {
        return valid && (this.credentialsChanged || this.validation.rolesChanged);
      }

      return false;
    },
    credentialsValid() {
      if (this.isCreate) {
        // Username must be supplied, password valid
        return !!this.form.username && this.validation.password;
      }
      if (this.isEdit) {
        // Password must be valid (though password field doesn't have to exist)
        return this.validation.password;
      }

      return false;
    },
    credentialsChanged() {
      if (this.isCreate) {
        return true; // Covered by valid
      }
      if (this.isEdit) {
        return !!this.form.password.password ||
          this.form.description !== this.value.description ||
          this.form.displayName !== this.value.displayName ||
          this.form.password.userChangeOnLogin !== this.value.mustChangePassword;
      }

      return false;
    },
    rolesValid() {
      return this.validation.roles;
    },
    isCreate() {
      return this.mode === _CREATE;
    },
    isEdit() {
      return this.mode === _EDIT;
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.name) {
        this.$refs.name.focus();
      }
    });
  },
  methods: {
    async save(buttonDone) {
      this.errors = [];
      try {
        if (this.isCreate) {
          const user = await this.createUser();

          await this.createSecret(user);
          await this.updateRoles(user);

          // Show success notification only after ALL operations complete
          // this is a "clone" of steve-class "processSaveResponse" toast/growl
          this.$store.dispatch('growl/success', {
            title:   this.t('generic.autogeneratedCreated.title', { resource: user.kind }),
            message: this.t('generic.autogeneratedCreated.message', { id: user.username, resource: user.kind }),
            timeout: 3000
          }, { root: true });
        } else {
          const user = await this.editUser();

          await this.updateRoles(user);
        }

        this.$router.replace({ name: this.doneRoute });
        buttonDone(true);
      } catch (err) {
        if (err?.message?.includes('errors due to escalation')) {
          this.errors = [this.t('rbac.errors.escalation')];
        } else {
          this.errors = exceptionToErrorsArray(err);
        }
        buttonDone(false);
      }
    },

    async createUser() {
      // never use "password" as it's deprecated!
      const user = await this.$store.dispatch('management/create', {
        type:               MANAGEMENT.USER,
        description:        this.form.description,
        enabled:            true,
        mustChangePassword: this.form.password.userChangeOnLogin,
        displayName:        this.form.displayName,
        username:           this.form.username
      });

      const userSaved = await user.save({
        // Don't show a success toast until the secret and GRB are also created
        suppressSuccessToast: true,
      });

      return userSaved;
    },

    async editUser() {
      if (!this.credentialsChanged) {
        return;
      }

      // never use this.value.password as it's deprecated!
      this.value.description = this.form.description;
      this.value.displayName = this.form.displayName;
      this.value.mustChangePassword = this.form.password.userChangeOnLogin;

      if (this.form.password.password) {
        await this.$refs.changePassword.save(this.value);
        // Why the wait? Without these the user updates below are ignored
        // - The update request succeeds and shows the correct values in it's response.
        // - Fetching the norman user again sometimes shows the correct value, sometimes not
        // - Even if the fetched norman user shows the correct value, it doesn't show up in the steve user
        //   - Looks like we re-request the stale version via socket?
        await wait(5000);
      }

      const user = await this.value.save();

      return user;
    },

    async createSecret(user) {
      if (this.form.password.password) {
        try {
        // create secret to hold user password
          const secret = await this.$store.dispatch('management/create', {
            type:     SECRET,
            metadata: {
              namespace: 'cattle-local-user-passwords',
              name:      user.id
            },
            data: { password: base64Encode(this.form.password.password) }
          });

          await secret.save();
        } catch (err) {
          if (this.isCreate) {
            try {
              // If secret creation fails, attempt to clean up the user to maintain consistency
              await user.remove();
            } catch (cleanupErr) {
              // Log cleanup error but prioritize original error for user feedback
              console.error('Failed to clean up user after secret creation failure:', cleanupErr); // eslint-disable-line no-console
            }
          }

          throw err;
        }
      }
    },

    async updateRoles(user) {
      if (!this.$refs.grb) {
        return;
      }

      const userId = user?.id || this.value.id;

      try {
        await this.$refs.grb.save(userId);
      } catch (err) {
        if (this.isCreate) {
          try {
            // If GRB creation fails, clean up the user to maintain consistency
            await user.remove();
          } catch (cleanupErr) {
            // Log cleanup error but prioritize original error for user feedback
            console.error('Failed to clean up user after GRB creation failure:', cleanupErr); // eslint-disable-line no-console
          }
        }
        throw err;
      }
    }
  }
};
</script>

<template>
  <Loading v-if="!value" />
  <CruResource
    v-else
    :done-route="doneRoute"
    :mode="mode"
    :resource="value"
    :validation-passed="valid"
    :errors="errors"
    :can-yaml="false"
    class="create-edit"
    @finish="save"
  >
    <div class="credentials">
      <h2> {{ t("user.edit.credentials.label") }}</h2>
      <div class="row">
        <div class="col span-4">
          <LabeledInput
            ref="name"
            v-model:value="form.username"
            label-key="user.edit.credentials.username.label"
            placeholder-key="user.edit.credentials.username.placeholder"
            :required="isCreate"
            :readonly="isEdit"
            :disabled="isView || isEdit"
            :ignore-password-managers="!isCreate"
          />
        </div>
        <div class="col span-4">
          <LabeledInput
            v-model:value="form.displayName"
            label-key="user.edit.credentials.displayName.label"
            placeholder-key="user.edit.credentials.displayName.placeholder"
            :disabled="isView"
          />
        </div>
      </div>
      <div class="row mt-20 mb-10">
        <div class="col span-8">
          <LabeledInput
            v-model:value="form.description"
            label-key="user.edit.credentials.userDescription.label"
            placeholder-key="user.edit.credentials.userDescription.placeholder"
            :disabled="isView"
          />
        </div>
      </div>

      <ChangePassword
        v-if="!isView"
        ref="changePassword"
        v-model:value="form.password"
        :mode="mode"
        :must-change-password="value.mustChangePassword"
        :user="value"
        @valid="validation.password = $event"
      />
    </div>
    <div
      v-if="showGlobalRoles"
      class="global-permissions"
    >
      <GlobalRoleBindings
        ref="grb"
        :user-id="value.id || liveValue.id"
        :mode="mode"
        :real-mode="realMode"
        type="user"
        @hasChanges="validation.rolesChanged = $event"
        @canLogIn="validation.roles = $event"
      />
    </div>
  </CruResource>
</template>
